/**
 * EN content catalog — the default and (at v1) the only content language.
 *
 * **Architecture:** root locale files are aggregators. Per-feature copy lives
 * in feature-owned `i18n/` modules (e.g. `features/landing/i18n/hero.en.ts`)
 * and is spread into the flat dot-keyed shape the `t()` helper consumes. Keep
 * this file thin — only app-level keys belong here directly.
 *
 * Keys are dot-namespaced by feature. Keep keys stable; English content is the
 * pivot for AR translation.
 */

import { landingEn, landingPlaceholdersEn } from '@/features/landing/i18n';
import { authEn } from '@/features/identity-management/auth/i18n';
import { earlyAccessEn } from '@/features/early-access/i18n';

/** App-level copy that is not owned by any feature. */
const appLevel = {
  'app.name': 'Workerize',
  'meta.title': 'Workerize | Talent Intelligence',
  'app.tagline':
    'Workerize is the intelligence layer that turns work into upward motion.',
  'common.loading': 'Loading…',
  'common.close': 'Close',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.create': 'Create',
  'common.edit': 'Edit',
  'common.view': 'View',
  'common.delete': 'Delete',
  'common.add': 'Add',
  'common.remove': 'Remove',
  'common.done': 'Done',
  'password.req.title': 'Password must include:',
  'password.req.length': 'At least 10 characters',
  'password.req.lowercase': 'A lowercase letter',
  'password.req.uppercase': 'An uppercase letter',
  'password.req.number': 'A number',
  'password.req.match': 'Passwords match',
  'password.req.met': 'met',
  'table.search': 'Search…',
  'table.columns': 'Columns',
  'table.index': '#',
  'table.filters': 'Filters',
  'table.filters.title': 'Filters',
  'table.filters.subtitle': 'Show only rows that match your conditions.',
  'table.filters.match': 'Match',
  'table.filters.all': 'all',
  'table.filters.any': 'any',
  'table.filters.ofConditions': 'of the conditions',
  'table.filters.field': 'Field',
  'table.filters.operator': 'Operator',
  'table.filters.value': 'Value',
  'table.filters.add': 'Add condition',
  'table.filters.removeCondition': 'Remove condition',
  'table.filters.apply': 'Apply filters',
  'table.filters.clear': 'Clear all',
  'table.columns.title': 'Columns',
  'table.columns.subtitle': 'Choose which columns to show.',
  'table.columns.reorderHint': 'Drag to reorder, or use the arrows. Toggle to show or hide.',
  'table.columns.dragHandle': 'Drag to reorder column',
  'table.columns.moveUp': 'Move up',
  'table.columns.moveDown': 'Move down',
  'table.value.true': 'Yes',
  'table.value.false': 'No',
  'table.op.eq': 'Equals',
  'table.op.ne': 'Not equals',
  'table.op.gt': 'Greater than',
  'table.op.lt': 'Less than',
  'table.op.gte': 'Greater or equal',
  'table.op.lte': 'Less or equal',
  'table.op.contains': 'Contains',
  'table.op.startsWith': 'Starts with',
  'table.op.endsWith': 'Ends with',
  'table.filter.any': 'Any',
  'table.filter.remove': 'Remove filter',

  // Invitations
  'invite.action': 'Invite user',
  'invite.title': 'Invite a user',
  'invite.subtitle': 'Send an email invitation with a role and a plan.',
  'invite.email': 'Email',
  'invite.roles': 'Role',
  'invite.roles.empty': 'No roles available.',
  'invite.plan': 'Plan',
  'invite.plan.empty': 'No active plans. Create one in Plans first.',
  'invite.plan.creditsSuffix': 'credits',
  'invite.credits': 'AI credits',
  'invite.credits.unlimited': 'Unlimited credits',
  'invite.credits.placeholder': 'Amount (optional)',
  'invite.submit': 'Send invitation',
  'invite.sent.title': 'Invitation sent',
  'invite.sent.body': 'We emailed an invite to',
  'invite.error.title': 'Could not send invitation',
  'invite.error.body': 'Something went wrong. Please try again.',
  'inviteAccept.title': 'Welcome to Workerize',
  'inviteAccept.subtitle': 'You’re setting up the account for',
  'inviteAccept.firstName': 'First name',
  'inviteAccept.lastName': 'Last name',
  'inviteAccept.submit': 'Create my account',
  'inviteAccept.submitting': 'Creating…',
  'inviteAccept.invalid.body':
    'This invitation link is invalid, has expired, or has already been used.',
  'inviteAccept.goToLogin': 'Go to sign in',
  'inviteAccept.done.title': 'Account created 🎉',
  'inviteAccept.done.body':
    'Check your email for your temporary password and sign-in link.',
  'inviteAccept.error.body': 'Could not create your account. Please try again.',
  'forcePw.title': 'Set a new password',
  'forcePw.subtitle':
    'Your account was created with a temporary password. Choose a new one to continue.',
  'forcePw.current': 'Temporary password',
  'forcePw.new': 'New password',
  'forcePw.confirm': 'Confirm new password',
  'forcePw.submit': 'Update password',
  'forcePw.submitting': 'Updating…',
  'forcePw.mismatch': 'Passwords do not match.',
  'forcePw.success': 'Password updated.',
  'forcePw.error': 'Could not update password.',
  'table.actions': 'Actions',
  'table.noResults': 'No results.',
  'table.error': 'Could not load this data.',
  'table.rowsPerPage': 'Rows per page',
  'table.prev': 'Previous page',
  'table.next': 'Next page',
} as const;

/** Foundation-owned UI affordances (status / router / chrome). */
const chrome = {
  'landing.placeholder.title': 'Workerize',
  'landing.placeholder.body': 'Creative Direction v1 in progress.',

  'theme.toggle.label': 'Toggle theme',
  'theme.toggle.light': 'Light theme',
  'theme.toggle.dark': 'Dark theme',
  'theme.toggle.to-light.label': 'Switch to light mode',
  'theme.toggle.to-dark.label': 'Switch to dark mode',

  'locale.toggle.label': 'Toggle language',
  'locale.toggle.en': 'English',
  'locale.toggle.ar': 'Arabic',
  'locale.toggle.aria.label': 'Switch language',
  'locale.short.en': 'EN',
  'locale.short.ar': 'AR',

  'nav.public.label': 'Landing page sections',
  'nav.public.platform': 'Platform',
  'nav.public.graphs': 'Graphs',
  'nav.public.candidates': 'Candidates',
  'nav.public.employers': 'Employers',

  'footer.tagline': 'AI-native talent intelligence.',
  'footer.region': 'MENA · GCC',
  'footer.col.platform.label': 'Footer platform navigation',
  'footer.col.platform.heading': 'Platform',
  'footer.col.legal.label': 'Footer legal navigation',
  'footer.col.legal.heading': 'Legal',
  'footer.col.region.heading': 'Region · Languages',
  'footer.legal.privacy': 'Privacy',
  'footer.legal.terms': 'Terms',
  'footer.legal.contact': 'Contact',
  'footer.entity.suffix': 'MENA-first.',
  'footer.entity.dec054': 'Built bilingual.',

  'a11y.skip-to-content': 'Skip to main content',

  'page-status.placeholder.label': 'Placeholder',
  'page-status.loading.label': 'Loading',
  'page-status.error.label': 'Error',
  'page-status.empty.label': 'Empty',
  'breadcrumb.label': 'Breadcrumb',
  'breadcrumb.home': 'Home',
  'breadcrumb.workspace': 'Workspace',
  'breadcrumb.profile': 'Profile',
  'toast.dismiss': 'Dismiss notification',

  'router.not-found.title': 'Page not found',
  'router.not-found.body': "The page you're looking for doesn't exist.",

  'router.error.title': 'Something went wrong',
  'router.error.body':
    'We hit an unexpected problem loading this page. Try again, or head back to the homepage.',
  'router.error.retry': 'Try again',
  'router.error.home': 'Back home',

  'router.unauthorized.title': 'Access denied',
  'router.unauthorized.body':
    "You don't have permission to view this page. If you think this is a mistake, contact your administrator.",
  'router.serviceDown.title': 'Service temporarily unavailable',
  'router.serviceDown.body':
    "We're having trouble reaching the service right now. Please try again in a moment.",

  'assistant.fab.label': 'Open the Workerize assistant',
  'assistant.title': 'Workerize Assistant',
  'assistant.subtitle': 'AI-native career guidance',
  'assistant.greeting':
    'Hi! I’m your Workerize career assistant. Ask me about your profile, target roles, or job search.',
  'assistant.placeholder': 'Ask anything…',
  'assistant.thinking': 'Thinking…',
  'assistant.disabled':
    'The AI assistant is not enabled on this deployment yet. Add an Anthropic API key to turn it on.',
  'assistant.error': 'Something went wrong. Please try again.',
  'assistant.outOfCredits':
    'You’re out of AI credits. Ask an admin to top up your balance.',
  'assistant.input.placeholder': 'Ask anything…',
  'assistant.input.label': 'Message the assistant',
  'assistant.send': 'Send',
  'assistant.close': 'Close assistant',
  'assistant.reply':
    'Workerize is not generating assistant guidance until real profile and CV evidence exists.',
  'assistant.reserved.title': 'Assistant waiting for real signals',
  'assistant.reserved.body':
    'Create your profile and upload a real CV first. Workerize will not show simulated AI guidance in the demo.',
  'assistant.reserved.input': 'Assistant input is reserved until real signals exist.',

  'appShell.topbar.title': 'Workspace',
  'appShell.topbar.subtitle': 'Talent intelligence, profile signals, and growth paths.',
  'appShell.topbar.tab.overview': 'Overview',
  'appShell.topbar.tab.analytics': 'Analytics',
  'appShell.topbar.tab.integrations': 'Integrations',
  'appShell.topbar.search.label': 'Quick search',
  'appShell.topbar.search.placeholder': 'Search…',
  'appShell.topbar.search.empty': 'No matching pages',
  'appShell.search.section.workspace': 'Workspace',
  'appShell.search.section.employer': 'Employer',
  'appShell.search.section.admin': 'Admin',
  'appShell.credits.label': 'AI credits',
  'appShell.credits.unlimited': 'Unlimited AI credits',
  'appShell.reserved.analytics.title': 'Analytics reserved',
  'appShell.reserved.analytics.body':
    'Analytics will appear after Workerize has real profile, CV, and activity history to summarize.',
  'appShell.reserved.integrations.title': 'Integrations reserved',
  'appShell.reserved.integrations.body':
    'Integrations are waiting for a later approved module. No fake integration status is shown.',
  'appShell.reserved.notifications.title': 'No notifications yet',
  'appShell.reserved.notifications.body':
    'Notification history will appear only after real workspace events exist.',
  'appShell.reserved.module.title': 'Module reserved',
  'appShell.reserved.module.body':
    'This workspace module is planned for a later approved phase and is not active in the demo.',
  'appShell.drawer.open': 'Open workspace navigation',
  'appShell.drawer.close': 'Close workspace navigation',
  'appShell.notifications.label': 'Notifications coming soon',
  'appShell.userMenu.open': 'Open user menu',
  'appShell.userMenu.loading': 'Loading account',
  'appShell.userMenu.nameUnavailable': 'Account details unavailable',
  'appShell.userMenu.emailUnavailable': 'Email unavailable',
  'appShell.sidebar.collapse': 'Collapse workspace navigation',
  'appShell.sidebar.expand': 'Expand workspace navigation',
  'appShell.nav.label': 'Workspace navigation',
  'appShell.nav.soon': 'Soon',
  'appShell.nav.chip.next': 'Next',
  'appShell.nav.chip.soon': 'Soon',
  'appShell.nav.section.workspace': 'Workspace',
  'appShell.nav.section.future': 'Reserved modules',
  'appShell.nav.workspace': 'Workspace',
  'appShell.nav.workspace.description': 'Your authenticated Workerize home.',
  'appShell.nav.profile': 'Profile',
  'appShell.nav.profile.description': 'Your real candidate profile intelligence.',
  'appShell.nav.smartProfile': 'Smart Profile',
  'appShell.nav.smartProfile.description': 'Profile intelligence and career signal graph.',
  'appShell.nav.cvIntelligence': 'CV Intelligence',
  'appShell.nav.cvIntelligence.description': 'Resume parsing and improvement guidance.',
  'appShell.nav.jobMatches': 'Job Matches',
  'appShell.nav.jobMatches.description': 'Role fit and opportunity matching.',
  'appShell.nav.applications': 'Applications',
  'appShell.nav.applications.description': 'Application memory and progress context.',
  'appShell.nav.company': 'Company',
  'appShell.nav.company.description': 'Employer-side talent intelligence.',
  'appShell.nav.settings': 'Settings',
  'appShell.nav.settings.description': 'Workspace preferences and account controls.',
  'appShell.nav.earlyAccess': 'Early access',
  'appShell.nav.earlyAccess.description': 'Review and manage waitlist requests.',
  'appShell.nav.admin': 'Admin',
  'appShell.nav.admin.description': 'Platform administration — users, roles, settings, metrics.',
  'appShell.nav.adminDashboard': 'Dashboard',
  'appShell.nav.adminDashboard.description': 'Live platform metrics overview.',
  'appShell.nav.adminUsers': 'User management',
  'appShell.nav.adminUsers.description': 'Manage user accounts, status, and actor types.',
  'appShell.nav.adminRoles': 'Role management',
  'appShell.nav.adminRoles.description': 'Roles and permission assignments.',
  'appShell.nav.adminPlans': 'Plans management',
  'appShell.nav.adminPlans.description': 'Subscription plans and entitlements.',
  'appShell.nav.adminAiUsage': 'AI usage',
  'appShell.nav.adminAiUsage.description': 'Token usage and AI call analytics.',
  'appShell.nav.adminEarlyAccess': 'Early access management',
  'appShell.nav.adminEarlyAccess.description': 'Review and manage waitlist requests.',
  'appShell.nav.adminSettings': 'System settings',
  'appShell.nav.adminSettings.description': 'Platform configuration and feature flags.',
  'appShell.nav.adminAuditLogs': 'Audit log',
  'appShell.nav.adminAuditLogs.description': 'Every action taken across the platform.',
  'admin.audit.title': 'Audit log',
  'admin.audit.col.time': 'Time',
  'admin.audit.col.actor': 'Actor',
  'admin.audit.col.action': 'Action',
  'admin.audit.col.module': 'Module',
  'admin.audit.col.status': 'Status',
  'admin.audit.searchPlaceholder': 'Search action, module…',
  'admin.audit.empty': 'No audit events yet.',
  'admin.audit.detailTitle': 'Audit event',
  'admin.audit.changes': 'What changed',
  'admin.audit.noChanges': 'No field-level changes were recorded for this event.',
  'admin.audit.field': 'Field',
  'admin.audit.before': 'Before',
  'admin.audit.after': 'After',
  'admin.audit.entity': 'Entity',
  'admin.audit.requestId': 'Request ID',
  'admin.audit.route': 'Route',

  // ---- Employer modules ----
  'appShell.nav.jobs': 'Jobs',
  'appShell.nav.jobs.description': 'Post and manage your job openings.',
  'appShell.nav.companyProfile': 'Company Profile',
  'appShell.nav.companyProfile.description': 'Your company details and logo.',
  'appShell.nav.findCandidates': 'Find Candidates',
  'appShell.nav.findCandidates.description': 'Search discoverable talent.',
  'appShell.nav.employerApplications': 'Applications',
  'appShell.nav.employerApplications.description': 'Applications to your jobs.',

  'appShell.nav.employerDashboard': 'Dashboard',
  'appShell.nav.employerDashboard.description': 'Your hiring overview at a glance.',

  // ---- Employer dashboard ----
  'employerDash.title': 'Dashboard',
  'employerDash.subtitle': 'Your hiring activity across jobs, applications, and offers.',
  'employerDash.metric.jobs': 'Jobs',
  'employerDash.metric.jobs.hint': 'Total job postings you own, across all statuses.',
  'employerDash.metric.published': 'Published jobs',
  'employerDash.metric.published.hint': 'Jobs currently visible to candidates.',
  'employerDash.metric.applications': 'Applications',
  'employerDash.metric.applications.hint': 'Total applications received to your jobs.',
  'employerDash.metric.review': 'In review',
  'employerDash.metric.review.hint': 'Applications you are actively reviewing.',
  'employerDash.metric.offers': 'Offers',
  'employerDash.metric.offers.hint': 'Offers you have extended to candidates.',
  'employerDash.recent': 'Recent applications',
  'employerDash.recent.empty': 'No applications yet.',
  'employerDash.error': 'Could not load your dashboard.',

  // ---- Restricted (no-workspace) actors ----
  'appShell.restricted.title': 'No workspace access',
  'appShell.restricted.body':
    'Your account type does not have a workspace yet. Please contact your administrator if you believe this is a mistake.',

  // ---- User menu ----
  'appShell.userMenu.account': 'Account settings',
  'appShell.userMenu.logout': 'Sign out',

  // ---- Account screen ----
  'account.title': 'Account',
  'account.subtitle': 'Manage your profile, picture, and password.',
  'account.profile.title': 'Profile',
  'account.profile.firstName': 'First name',
  'account.profile.lastName': 'Last name',
  'account.profile.email': 'Email',
  'account.profile.emailHint': 'Your sign-in email cannot be changed here.',
  'account.profile.locale': 'Language',
  'account.profile.save': 'Save changes',
  'account.profile.saved': 'Profile updated.',
  'account.profile.error': 'Could not update your profile.',
  'account.avatar.title': 'Profile picture',
  'account.avatar.upload': 'Upload image',
  'account.avatar.remove': 'Remove',
  'account.avatar.hint': 'PNG, JPEG, or WEBP. Max 5 MB.',
  'account.avatar.saved': 'Profile picture updated.',
  'account.avatar.removed': 'Profile picture removed.',
  'account.avatar.error': 'Could not update your picture.',
  'account.password.title': 'Change password',
  'account.password.current': 'Current password',
  'account.password.new': 'New password',
  'account.password.confirm': 'Confirm new password',
  'account.password.submit': 'Update password',
  'account.password.changed': 'Password changed. Please sign in again.',
  'account.password.mismatch': 'The new passwords do not match.',
  'account.password.weak':
    'Use at least 10 characters with an uppercase letter, a lowercase letter, and a digit.',
  'account.password.required': 'This field is required.',
  'account.password.error': 'Could not change your password.',
  'account.password.oauthOnly':
    'Your account signs in with a social provider and has no password to change.',
  'account.privacy.link': 'Privacy & discoverability',
  'account.privacy.hint': 'Control who can discover your profile.',

  // ---- Company logo (employer) ----
  'company.logo.title': 'Company logo',
  'company.logo.upload': 'Upload logo',
  'company.logo.remove': 'Remove',
  'company.logo.hint': 'PNG, JPEG, or WEBP. Max 5 MB.',
  'company.logo.saved': 'Logo updated.',
  'company.logo.removed': 'Logo removed.',
  'company.logo.error': 'Could not update the logo.',

  // ---- Admin OS ----
  'admin.loading': 'Loading…',
  'admin.loadError': 'Could not load this data.',
  'admin.dashboard.title': 'Admin dashboard',
  'admin.dashboard.subtitle': 'Live platform metrics and management.',
  'admin.dashboard.eyebrow': 'Platform intelligence',
  'admin.dashboard.aiActivity': 'AI activity',
  'admin.dashboard.aiAvg': 'Avg tokens / call',
  'admin.dashboard.aiByFeature': 'Top features by tokens',
  'admin.dashboard.aiSource.langfuse': 'via Langfuse',
  'admin.dashboard.aiSource.local': 'Local data',
  'admin.nav.users': 'Users',
  'admin.nav.roles': 'Roles & permissions',
  'admin.nav.plans': 'Plans',
  'admin.nav.aiUsage': 'AI usage',
  'admin.nav.settings': 'System settings',
  'admin.nav.earlyAccess': 'Early access',
  'admin.kpi.users': 'Users',
  'admin.kpi.companies': 'Companies',
  'admin.kpi.jobs': 'Jobs',
  'admin.kpi.applications': 'Applications',
  'admin.kpi.earlyAccess': 'Early-access requests',
  'admin.kpi.cvDocuments': 'CV documents',
  'admin.kpi.aiTokens': 'AI tokens',
  'admin.kpi.aiCalls': 'AI calls',
  'admin.kpi.users.hint': 'Total registered accounts across all actor types.',
  'admin.kpi.companies.hint': 'Employer company profiles created on the platform.',
  'admin.kpi.jobs.hint': 'Total job postings created by employers.',
  'admin.kpi.applications.hint': 'Total candidate applications submitted to jobs.',
  'admin.kpi.earlyAccess.hint': 'Waitlist requests received (new, reviewed, invited, rejected).',
  'admin.kpi.cvDocuments.hint': 'CVs uploaded by candidates.',
  'admin.kpi.aiTokens.hint': 'Total AI tokens consumed across all AI-backed features.',
  'admin.kpi.aiCalls.hint': 'Total AI calls made across all AI-backed features.',
  'admin.breakdown.usersByActor': 'Users by actor type',
  'admin.breakdown.usersByStatus': 'Users by status',
  'admin.breakdown.jobsByStatus': 'Jobs by status',
  'admin.breakdown.applicationsByStatus': 'Applications by status',
  'admin.breakdown.earlyAccessByStatus': 'Early access by status',
  'admin.breakdown.applicationsByStatus.hint':
    'Applications across the hiring funnel — new → reviewing → offered → accepted → rejected.',
  'admin.breakdown.usersByStatus.hint': 'Accounts grouped by status (active, pending, suspended…).',
  'admin.breakdown.usersByActor.hint': 'Share of accounts by actor type (candidate, employer, admin…).',
  'admin.breakdown.jobsByStatus.hint': 'Job postings grouped by their current status.',
  'admin.breakdown.earlyAccessByStatus.hint': 'Waitlist requests grouped by review status.',
  'admin.users.title': 'Users',
  'admin.users.subtitle': 'Manage accounts, roles, actor types, and AI credits.',
  'admin.users.saveError': 'Could not save the change.',
  'admin.users.col.user': 'User',
  'admin.users.col.actorType': 'Actor type',
  'admin.users.col.status': 'Status',
  'admin.users.actorType.none': 'None',
  'admin.users.manage': 'Manage',
  'admin.users.close': 'Close',
  'admin.users.roles': 'Roles',
  'admin.users.saveRoles': 'Save roles',
  'admin.users.credits': 'AI credits',
  'admin.users.unlimited': 'Unlimited',
  'admin.users.grantPlaceholder': 'Amount',
  'admin.users.grant': 'Grant',
  'admin.users.makeUnlimited': 'Make unlimited',
  'admin.users.revokeUnlimited': 'Revoke unlimited',
  'admin.users.plan': 'Plan',
  'admin.users.noPlan': 'No plan',
  'admin.users.applyPlan': 'Apply plan',
  'admin.users.planApplied': 'Plan applied — credits updated.',
  'admin.roles.title': 'Roles & permissions',
  'admin.roles.subtitle': 'Adjust which permissions each role grants.',
  'admin.roles.saved': 'Permissions saved.',
  'admin.roles.saveError': 'Could not save permissions.',
  'admin.roles.system': 'system',
  'admin.roles.permsLabel': 'permissions',
  'admin.roles.save': 'Save permissions',
  'admin.aiUsage.title': 'AI usage',
  'admin.aiUsage.subtitle': 'Token consumption across AI-backed features.',
  'admin.aiUsage.calls': 'Total calls',
  'admin.aiUsage.inputTokens': 'Input tokens',
  'admin.aiUsage.outputTokens': 'Output tokens',
  'admin.aiUsage.totalTokens': 'Total tokens',
  'admin.aiUsage.empty': 'No AI usage recorded yet.',
  'admin.aiUsage.costPerCredit': 'Cost / credit',
  'admin.aiUsage.estimatedCost': 'Estimated AI cost',
  'admin.aiUsage.monthlyRevenue': 'Monthly revenue (MRR)',
  'admin.aiUsage.grossMargin': 'Est. gross margin',
  'admin.aiUsage.col.feature': 'Feature',
  'admin.aiUsage.col.model': 'Model',
  'admin.aiUsage.col.tokens': 'Tokens',
  'admin.aiUsage.col.when': 'When',
  'admin.settings.title': 'System settings',
  'admin.settings.subtitle': 'Runtime configuration values.',
  'admin.settings.saved': 'Setting saved.',
  'admin.settings.saveError': 'Could not save the setting.',
  'admin.settings.empty': 'No settings defined.',
  'admin.settings.save': 'Save',
  'admin.settings.col.key': 'Key',
  'admin.settings.col.type': 'Type',
  'admin.settings.col.value': 'Value',
  'admin.plans.title': 'Subscription plans',
  'admin.plans.subtitle': 'Define plans and their AI-credit allotments.',
  'admin.plans.created': 'Plan created.',
  'admin.plans.saveError': 'Could not save the plan.',
  'admin.plans.empty': 'No plans yet — create one above.',
  'admin.plans.field.code': 'Code',
  'admin.plans.field.name': 'Name',
  'admin.plans.field.credits': 'Monthly credits',
  'admin.plans.field.price': 'Price (USD)',
  'admin.plans.field.interval': 'Billing',
  'admin.plans.field.features': 'Features',
  'admin.plans.field.policies': 'Policies',
  'admin.plans.features.placeholder': 'Add a feature…',
  'admin.plans.policies.placeholder': 'Add a policy…',
  'admin.plans.perCredit': '$ / credit',
  'admin.plans.perCredit.na': 'Set a price and credits to see the $/credit rate.',
  'admin.plans.margin': 'Margin',
  'admin.plans.margin.na': 'Set a cost per credit (in Settings) to see margin.',
  'admin.plans.interval.monthly': 'monthly',
  'admin.plans.interval.yearly': 'yearly',
  'admin.plans.interval.one_time': 'one-time',
  'admin.plans.create': 'Create plan',
  'admin.plans.creditsLabel': 'credits',
  'admin.plans.deactivate': 'Deactivate',
  'admin.plans.activate': 'Activate',

  // ---- Admin redesign (DataTable + modals) ----
  'admin.users.col.verified': 'Verified',
  'admin.users.col.roles': 'Roles',
  'admin.users.col.created': 'Created',
  'admin.users.col.lastLogin': 'Last login',
  'admin.users.edit': 'Edit user',
  'admin.users.view': 'User details',
  'admin.users.firstName': 'First name',
  'admin.users.lastName': 'Last name',
  'admin.users.verified': 'Email verified',
  'admin.users.saved': 'User updated.',
  'admin.users.never': 'Never',
  'admin.roles.edit': 'Edit role',
  'admin.roles.col.role': 'Role',
  'admin.roles.col.code': 'Code',
  'admin.roles.col.perms': 'Permissions',
  'admin.plans.add': 'New plan',
  'admin.plans.edit': 'Edit plan',
  'admin.plans.saved': 'Plan saved.',
  'admin.plans.field.description': 'Description',
  'admin.plans.field.active': 'Active',
  'admin.plans.col.status': 'Status',
  'admin.aiUsage.col.user': 'User',
  'admin.aiUsage.col.status': 'Status',
  'admin.aiUsage.status.ok': 'Success',
  'admin.aiUsage.status.fail': 'Failed',
  'admin.settings.edit': 'Edit setting',

  // ---- Job matches (real) ----
  'jobMatches.title': 'Job Matches',
  'jobMatches.subtitle': 'Roles matched to your skills from live postings.',
  'jobMatches.loading': 'Finding matches…',
  'jobMatches.error': 'Could not load matches.',
  'jobMatches.empty': 'No matches yet',
  'jobMatches.emptyBody': 'Add skills to your profile, or check back as employers post roles.',
  'jobMatches.matched': 'Matched',
  'jobMatches.missing': 'Missing',
  'jobMatches.apply': 'Apply',
  'jobMatches.appliedShort': 'Applied',
  'jobMatches.applied': 'Application sent',
  'jobMatches.applyError': 'Could not apply to this job.',

  // ---- Applications (candidate, real) ----
  'applications.title': 'Applications',
  'applications.subtitle': 'Track your job applications and offers.',
  'applications.loading': 'Loading…',
  'applications.error': 'Could not load your applications.',
  'applications.offers': 'Offers received',
  'applications.noOffers': 'No offers yet.',
  'applications.accept': 'Accept',
  'applications.decline': 'Decline',
  'applications.mine': 'My applications',
  'applications.empty': 'No applications yet',
  'applications.emptyBody': 'Apply to roles from Job Matches to see them here.',
  'applications.col.job': 'Job',
  'applications.col.status': 'Status',
  'applications.withdraw': 'Withdraw',
  'applications.actionError': 'Action failed. Please try again.',
  'applicationStatus.new': 'New',
  'applicationStatus.reviewing': 'Reviewing',
  'applicationStatus.offered': 'Offered',
  'applicationStatus.accepted': 'Accepted',
  'applicationStatus.rejected': 'Not selected',
  'applicationStatus.withdrawn': 'Withdrawn',
  'offerStatus.sent': 'Awaiting your response',
  'offerStatus.accepted': 'Accepted',
  'offerStatus.declined': 'Declined',
  'offerStatus.withdrawn': 'Withdrawn',

  // ---- Notifications (real) ----
  'notifications.label': 'Notifications',
  'notifications.title': 'Notifications',
  'notifications.empty': 'No notifications yet',
  'notifications.markAll': 'Mark all read',
  'notifications.tab.all': 'All',
  'notifications.tab.unread': 'Unread',
  'notifications.tab.read': 'Read',
  'notifications.seeMore': 'See more',
  'notifications.seeLess': 'See less',
  'notifications.from': 'From',

  'appShell.sidebar.status.label': 'Shell status',
  'appShell.sidebar.status.title': 'Workspace foundation ready',
  'appShell.sidebar.status.body':
    'Live product modules will attach here as approved Workerize surfaces ship.',

  'appHome.eyebrow': 'Authenticated workspace',
  'appHome.greeting.named': 'Welcome back,',
  'appHome.greeting.default': 'Welcome back to Workerize',
  'appHome.intro':
    'This is the calm starting point for your Workerize intelligence workspace. Future modules will mount here without changing the shell.',
  'appHome.card.smartProfile.title': 'Smart Profile',
  'appHome.card.smartProfile.body':
    'A future space for profile signals, strengths, and career direction.',
  'appHome.card.smartProfile.hint': 'Profile signals will appear here.',
  'appHome.card.cvIntelligence.title': 'CV Intelligence',
  'appHome.card.cvIntelligence.body':
    'A future space for structured CV understanding and improvement prompts.',
  'appHome.card.cvIntelligence.hint': 'CV analysis module coming next.',
  'appHome.card.jobMatches.title': 'Job Matches',
  'appHome.card.jobMatches.body':
    'A future space for fit-aware opportunities without turning Workerize into a job board.',
  'appHome.card.jobMatches.hint': 'Fit-aware matches will appear here.',
  'appHome.card.reserved': 'Reserved',
  'appHome.workspaceState.label': 'Foundation state',
  'appHome.workspaceState.profile': 'Profile layer reserved',
  'appHome.workspaceState.cv': 'CV intelligence reserved',
  'appHome.workspaceState.matches': 'Match graph reserved',
  'appHome.insight.readiness.title': 'Profile readiness',
  'appHome.insight.readiness.body':
    'This panel is reserved for readiness guidance once the talent profile module exists.',
  'appHome.insight.nextAction.title': 'Next action',
  'appHome.insight.nextAction.body':
    'The next approved module will replace this reserved panel with real guidance.',
  'appHome.insight.ai.title': 'AI assistant status',
  'appHome.insight.ai.body':
    'Workerize will keep guidance contextual as real profile and CV signals arrive.',
  'appHome.insight.chip': 'Coming soon',

  'appHome.empty.intro':
    'Your workspace is ready. Real profile, CV, and intelligence signals will appear here only after you create them.',
  'appHome.empty.status': 'Empty',
  'appHome.empty.profileReadiness.label': 'Profile intelligence',
  'appHome.empty.profileReadiness.title':
    'Build your profile to unlock profile intelligence.',
  'appHome.empty.profileReadiness.body':
    'Workerize has not received profile or CV evidence yet, so readiness scores and trends stay hidden until real data exists.',
  'appHome.empty.profileReadiness.step.identity': 'Identity connected',
  'appHome.empty.profileReadiness.step.profile': 'Profile not created',
  'appHome.empty.profileReadiness.step.cv': 'CV not uploaded',
  'appHome.empty.nextAction.label': 'Next action',
  'appHome.empty.nextAction.title': 'Upload your CV to start CV analysis.',
  'appHome.empty.nextAction.body':
    'CV insights will appear after a real CV is uploaded and analyzed. No recommendations are generated before that.',
  'appHome.empty.nextAction.status': 'Awaiting real CV',
  'appHome.empty.nextAction.action': 'Go to CV upload',
  'appHome.empty.profile.title': 'Smart Profile',
  'appHome.empty.profile.body':
    'Profile intelligence will appear after your candidate profile is created.',
  'appHome.empty.profile.action': 'Build your profile',
  'appHome.empty.cv.title': 'CV Intelligence',
  'appHome.empty.cv.body':
    'CV insights will appear after a real CV is uploaded and analyzed.',
  'appHome.empty.cv.action': 'Upload or analyze CV',
  'appHome.empty.applications.title': 'Application Intelligence',
  'appHome.empty.applications.body':
    'No application intelligence yet. Workerize will only show application context after real activity exists.',
  'appHome.empty.applications.action': 'Application tracking is reserved for a later approved module.',
  'appHome.reserved.applications.title': 'Application intelligence reserved',
  'appHome.reserved.applications.body':
    'Workerize will only show application context after the approved application module records real activity.',
  'appHome.empty.applicationsPanel.title': 'Application intelligence',
  'appHome.empty.applicationsPanel.body':
    'This area is reserved for real application memory and progress context.',
  'appHome.empty.applicationsPanel.emptyTitle': 'No application intelligence yet.',
  'appHome.empty.applicationsPanel.emptyBody':
    'Workerize is not showing fake application rows, match scores, or stage signals. Real rows will appear after the relevant module records activity.',
  'appHome.empty.rail.label': 'Intelligence context',
  'appHome.empty.rail.intro':
    'This rail will summarize real profile, CV, and workspace signals after they exist.',
  'appHome.empty.rail.assistant.title': 'AI assistant status',
  'appHome.empty.rail.assistant.body':
    'Contextual guidance is waiting for real profile and CV signals.',
  'appHome.empty.rail.recommendation.title': 'Recommended action',
  'appHome.empty.rail.recommendation.body':
    'No AI recommendation is available yet because no real CV or profile evidence has been analyzed.',
  'appHome.empty.rail.trend.title': 'Signal trends',
  'appHome.empty.rail.trend.body':
    'No fabricated percentages or trend lines are shown before real history exists.',
  'appHome.empty.rail.network.title': 'Signal network',
  'appHome.empty.rail.network.body':
    'Profile, CV, application, and growth signals will connect here after the real modules ship.',
  'appHome.empty.rail.footer': 'Reserved intelligence panels',
  'appHome.empty.rail.footerStatus': 'Empty state',

  'candidateProfile.eyebrow': 'Profile intelligence',
  'candidateProfile.loading.title': 'Loading profile',
  'candidateProfile.loading.body': 'Reading your saved Workerize profile data.',
  'candidateProfile.error.title': 'Profile could not load',
  'candidateProfile.action.retry': 'Retry',
  'candidateProfile.action.save': 'Save profile',
  'candidateProfile.action.reset': 'Reset changes',
  'candidateProfile.action.add': 'Add',
  'candidateProfile.action.remove': 'Remove',
  'candidateProfile.save.saving': 'Saving',
  'candidateProfile.save.success': 'Profile saved.',
  'candidateProfile.save.success.body': 'Your real profile changes are now saved.',
  'candidateProfile.save.error.title': 'Profile could not be saved',
  'candidateProfile.save.error.body': 'Review the message at the top of the form and try again.',
  'candidateProfile.validation.error.title': 'Check the profile details',
  'candidateProfile.validation.years':
    'Years of experience must be a whole number between 0 and 80.',
  'candidateProfile.empty.displayName': 'Your name',
  'candidateProfile.empty.headline': 'Add a headline for your profile.',
  'candidateProfile.empty.state': 'No profile created yet.',
  'candidateProfile.empty.body':
    'Workerize has not stored a candidate profile yet. Add your real details and save to create it.',
  'candidateProfile.empty.skills': 'No skills saved yet.',
  'candidateProfile.empty.experience': 'No experience saved yet.',
  'candidateProfile.empty.education': 'No education saved yet.',
  'candidateProfile.empty.projects': 'No projects saved yet.',
  'candidateProfile.empty.goals': 'No goals saved yet.',
  'candidateProfile.completeness.label': 'Completeness',
  'candidateProfile.completeness.complete': 'All tracked profile sections have data.',
  'candidateProfile.apply.toast.title': 'CV suggestions applied',
  'candidateProfile.apply.toast.body':
    'Empty fields were filled and new skills added from your CV. Review them, then Save to keep the changes.',
  'candidateProfile.apply.none.title': 'Nothing to apply',
  'candidateProfile.apply.none.body':
    'Your profile already covers the suggestions from this CV.',
  'candidateProfile.nav.label': 'On this page',
  'candidateProfile.checklist.label': 'Completion checklist',
  'candidateProfile.checklist.done': 'Added',
  'candidateProfile.checklist.todo': 'Not added yet',
  'candidateProfile.section.overview': 'Header and overview',
  'candidateProfile.section.overview.body':
    'These details shape the profile that future CV and intelligence signals will build on.',
  'candidateProfile.section.summary': 'Career summary',
  'candidateProfile.section.summary.body':
    'Keep this concise and evidence-based so Workerize can understand your career direction.',
  'candidateProfile.section.skills': 'Skills',
  'candidateProfile.section.skills.body':
    'Add skills manually for now; future approved modules may enrich them from real CV evidence.',
  'candidateProfile.section.experience': 'Experience',
  'candidateProfile.section.experience.body':
    'Experience entries help Workerize understand the career evidence behind your profile.',
  'candidateProfile.section.education': 'Education',
  'candidateProfile.section.education.body':
    'Add real education history that supports your career and skill signals.',
  'candidateProfile.section.projects': 'Projects',
  'candidateProfile.section.projects.body':
    'Projects capture practical evidence without inventing achievements or recommendations.',
  'candidateProfile.section.goals': 'Goals',
  'candidateProfile.section.goals.body':
    'Goals help future approved guidance stay aligned with your intended direction.',
  'candidateProfile.field.displayName': 'Display name',
  'candidateProfile.field.headline': 'Headline',
  'candidateProfile.field.currentRole': 'Current role',
  'candidateProfile.field.location': 'Location',
  'candidateProfile.field.preferredLanguage': 'Preferred language',
  'candidateProfile.field.summary': 'Summary',
  'candidateProfile.field.careerLevel': 'Career level',
  'candidateProfile.field.yearsOfExperience': 'Years of experience',
  'candidateProfile.field.dateOfBirth': 'Date of birth',
  'candidateProfile.field.gender': 'Gender',
  'candidateProfile.option.gender.unspecified': 'Prefer not to say',
  'candidateProfile.field.skillName': 'Skill name',
  'candidateProfile.field.title': 'Title',
  'candidateProfile.field.company': 'Company',
  'candidateProfile.field.startDate': 'Start date',
  'candidateProfile.field.endDate': 'End date',
  'candidateProfile.field.isCurrent': 'Current role',
  'candidateProfile.field.description': 'Description',
  'candidateProfile.field.institution': 'Institution',
  'candidateProfile.field.degree': 'Degree',
  'candidateProfile.field.fieldOfStudy': 'Field of study',
  'candidateProfile.field.projectName': 'Project name',
  'candidateProfile.field.url': 'URL',
  'candidateProfile.field.technologies': 'Technologies',
  'candidateProfile.field.goalTitle': 'Goal title',
  'candidateProfile.field.targetDate': 'Target date',
  'candidateProfile.field.status': 'Status',
  'candidateProfile.option.level.none': 'No level',
  'candidateProfile.option.status.planned': 'Planned',
  'candidateProfile.option.status.inProgress': 'In progress',
  'candidateProfile.option.status.completed': 'Completed',
  'candidateProfile.panel.snapshot': 'Saved profile snapshot',
  'candidateProfile.panel.save': 'Persist changes',
  'candidateProfile.panel.save.body':
    'Saving updates this profile with the backend response. Empty optional fields stay empty.',

  'candidateCv.eyebrow': 'CV intelligence',
  'candidateCv.title': 'CV upload, extraction, and analysis',
  'candidateCv.body':
    'Upload a real PDF or DOCX CV, extract its text, then analyze it against your saved profile.',
  'candidateCv.loading': 'Loading CV intelligence',
  'candidateCv.upload.label': 'Upload CV',
  'candidateCv.upload.hint': 'PDF or DOCX only. Maximum file size: 10 MB.',
  'candidateCv.action.upload': 'Upload CV',
  'candidateCv.action.replace': 'Replace CV',
  'candidateCv.action.delete': 'Delete CV',
  'candidateCv.action.deleting': 'Deleting',
  'candidateCv.action.confirmDelete': 'Confirm delete CV',
  'candidateCv.action.cancelDelete': 'Cancel',
  'candidateCv.action.extract': 'Extract CV text',
  'candidateCv.action.extracting': 'Extracting',
  'candidateCv.action.analyze': 'Analyze CV',
  'candidateCv.action.analyzing': 'Analyzing',
  'candidateCv.empty.title': 'No CV uploaded yet.',
  'candidateCv.empty.body':
    'Upload a real CV to unlock extraction and profile-aware analysis.',
  'candidateCv.current.untitled': 'Current CV',
  'candidateCv.current.words': 'Words',
  'candidateCv.current.characters': 'Characters',
  'candidateCv.status.notStarted': 'Not extracted',
  'candidateCv.status.extracting': 'Extracting',
  'candidateCv.status.extracted': 'Extracted',
  'candidateCv.status.extractionFailed': 'Extraction failed',
  'candidateCv.error.noFile': 'Choose a PDF or DOCX file before uploading.',
  'candidateCv.error.unsupported': 'Only PDF and DOCX files are supported.',
  'candidateCv.error.tooLarge': 'CV files must be 10 MB or smaller.',
  'candidateCv.toast.upload.success': 'CV uploaded.',
  'candidateCv.toast.upload.error': 'CV could not be uploaded',
  'candidateCv.toast.extract.success': 'CV text extracted.',
  'candidateCv.toast.extract.error': 'CV text could not be extracted',
  'candidateCv.toast.analyze.success': 'CV analysis complete.',
  'candidateCv.toast.analyze.error': 'CV could not be analyzed',
  'candidateCv.toast.delete.success': 'CV deleted.',
  'candidateCv.toast.delete.error': 'CV could not be deleted',
  'candidateCv.delete.confirm.title': 'Delete this CV?',
  'candidateCv.delete.confirm.body':
    'This removes the visible uploaded CV metadata and analysis state for your profile.',
  'candidateCv.analysis.empty.title': 'No analysis yet.',
  'candidateCv.analysis.empty.body':
    'Extract the CV text first, then run analysis to see real backend results.',
  'candidateCv.analysis.title': 'Analysis result',
  'candidateCv.analysis.mode.ai': 'AI',
  'candidateCv.analysis.mode.ruleBased': 'Rule-based',
  'candidateCv.analysis.mode.fallback': 'Fallback',
  'candidateCv.analysis.fallbackNotice':
    'Analysis completed with fallback mode.',
  'candidateCv.analysis.ats': 'ATS readiness',
  'candidateCv.analysis.profileStrength': 'Profile strength',
  'candidateCv.analysis.strengths': 'Strengths',
  'candidateCv.analysis.weaknesses': 'Weaknesses',
  'candidateCv.analysis.missingSections': 'Missing sections',
  'candidateCv.analysis.detectedSkills': 'Detected skills',
  'candidateCv.analysis.suggestedSkills': 'Suggested skills',
  'candidateCv.analysis.recommendations': 'Recommendations',
  'candidateCv.analysis.provider.none': 'No external AI provider used',
  'candidateCv.priority.high': 'High',
  'candidateCv.priority.medium': 'Medium',
  'candidateCv.priority.low': 'Low',
  'candidateCv.apply.action': 'Apply to profile',
  'candidateCv.apply.hint':
    'Fills empty profile fields and adds new skills for your review.',

  // ── Workspace command center (real-data dashboard rethink) ──
  'appHome.command.readiness.label': 'Profile readiness',
  'appHome.command.readiness.suffix': 'complete',
  'appHome.command.readiness.empty':
    'Build your profile to unlock readiness intelligence.',
  'appHome.command.readiness.loading': 'Reading your profile signal…',
  'appHome.command.nextAction.label': 'Next best action',
  'appHome.command.action.build.body':
    'Create your candidate profile so Workerize can read your real skills and experience.',
  'appHome.command.action.build.cta': 'Build your profile',
  'appHome.command.action.continue.body':
    'A few sections are still missing. Complete them to strengthen your profile signal.',
  'appHome.command.action.continue.cta': 'Continue your profile',
  'appHome.command.action.explore.body':
    'Your profile is complete. Preview the AI matching experience that activates as the talent graph fills in.',
  'appHome.command.action.explore.cta': 'Preview Job Matches',
  'appHome.command.intel.label': 'Your intelligence',
  'appHome.command.intel.intro':
    'Live modules use your real data. Preview modules show what activates in a later approved phase.',
  'appHome.command.card.live': 'Live',
  'appHome.command.card.preview': 'Preview',
  'appHome.command.card.cta.live': 'Open',
  'appHome.command.card.cta.preview': 'See preview',

  // ── Feature preview surfaces (reserved PRD modules made navigable) ──
  'featurePreview.chip.preview': 'Preview',
  'featurePreview.sample.badge': 'Sample data',
  'featurePreview.sample.note': 'Illustrative only — not your real data.',
  'featurePreview.whatItDoes': 'What this will do',

  // Job Matches — PRD Module 2 (AI Matching Engine)
  'preview.jobMatches.eyebrow': 'Module 2 · AI Matching Engine',
  'preview.jobMatches.title': 'Job Matches',
  'preview.jobMatches.body':
    'Semantic, explainable role matching — not keyword search and not a job board. Workerize surfaces roles that fit your real skills, including adjacent and inferred ones.',
  'preview.jobMatches.bullet.1':
    'Vector-based matching on your skills, seniority, and trajectory — beyond keyword overlap.',
  'preview.jobMatches.bullet.2':
    'Explainable fit scores with the strengths and gaps behind each match.',
  'preview.jobMatches.bullet.3':
    'Surfaces adjacent and inferred capabilities, not just literal title matches.',
  'preview.jobMatches.bullet.4':
    'Hidden-match signals so strong roles can find you, subject to your visibility settings.',
  'preview.jobMatches.phase':
    'Job Matches activates once the AI Matching Engine and Talent Graph reach the density to make recommendations credible. No matches are fabricated before then.',
  'preview.jobMatches.sample.role': 'Senior Backend Engineer',
  'preview.jobMatches.sample.company': 'Sample Company',
  'preview.jobMatches.sample.fit': '88% fit',
  'preview.jobMatches.sample.reason': 'Strong on Node.js, NestJS, MongoDB · gap: Kubernetes',

  // Applications — PRD Module 3 (Hiring OS, candidate side)
  'preview.applications.eyebrow': 'Module 3 · Hiring OS',
  'preview.applications.title': 'Applications',
  'preview.applications.body':
    'A living memory of every role you pursue — stage, context, and next step — instead of sending applications into a black hole.',
  'preview.applications.bullet.1':
    'Track each application through a clear pipeline: Applied → Shortlisted → Interview → Offer.',
  'preview.applications.bullet.2':
    'See where you stand and what the next step is, with real status from employers.',
  'preview.applications.bullet.3':
    'Keep the job description, your fit explanation, and notes together per application.',
  'preview.applications.bullet.4':
    'Receive nudges tied to real activity — never fabricated progress.',
  'preview.applications.phase':
    'Applications activates with the Employer Hiring OS so status reflects real pipeline activity. Until then no application rows are shown.',
  'preview.applications.sample.role': 'Product Designer',
  'preview.applications.sample.stage': 'Shortlisted',
  'preview.applications.sample.role2': 'Frontend Engineer',
  'preview.applications.sample.stage2': 'Interview',

  // Smart Profile — PRD Module 1 + 4 (Talent Identity + Career Intelligence)
  'preview.smartProfile.eyebrow': 'Module 1 · Talent Identity',
  'preview.smartProfile.title': 'Smart Profile',
  'preview.smartProfile.body':
    'Your profile, read by AI — a Skill Graph and an Employability Score with a clear, measurable path to improve it.',
  'preview.smartProfile.bullet.1':
    'A normalized Skill Graph: hard, soft, inferred, and adjacent skills with confidence.',
  'preview.smartProfile.bullet.2':
    'A versioned Employability Score with an explainable factor breakdown.',
  'preview.smartProfile.bullet.3':
    'Skill-gap analysis against target roles and a clear next step to improve.',
  'preview.smartProfile.bullet.4':
    'Built on your real profile and CV — no score is shown before real evidence exists.',
  'preview.smartProfile.phase':
    'Smart Profile activates once the Skill Graph and Employability Score engine ship. Your real profile and CV Intelligence are already live under Profile.',
  'preview.smartProfile.sample.score': 'Employability Score',
  'preview.smartProfile.sample.value': '72 / 100',
  'preview.smartProfile.sample.factor1': 'Skill coverage',
  'preview.smartProfile.sample.factor2': 'Profile completeness',
  'preview.smartProfile.sample.factor3': 'Market demand',

  // Smart Profile — real-data view
  'smartProfile.loading': 'Loading your Smart Profile…',
  'smartProfile.empty.title': 'Build your profile to unlock Smart Profile',
  'smartProfile.empty.body':
    'Smart Profile reads your real profile and CV. Add your details to see your skill map, experience, and profile strength.',
  'smartProfile.empty.cta': 'Build your profile',
  'smartProfile.score.strength': 'Profile strength',
  'smartProfile.score.completeness': 'Profile completeness',
  'smartProfile.score.employability': 'Employability Score',
  'smartProfile.score.employabilityReserved': 'Employability Score — reserved for a later phase',
  'smartProfile.score.version': 'v1 · computed from your profile & CV',
  'smartProfile.score.computing': 'Calculating your Employability Score…',
  'smartProfile.breakdown.title': 'Score breakdown',
  'smartProfile.breakdown.note': 'How your Employability Score is reached.',
  'smartProfile.breakdown.cvHint': 'Analyze a CV to include CV readiness.',
  'smartProfile.factor.completeness': 'Profile completeness',
  'smartProfile.factor.skills': 'Skills depth',
  'smartProfile.factor.experience': 'Experience',
  'smartProfile.factor.education': 'Education & projects',
  'smartProfile.factor.cv': 'CV readiness',
  'smartProfile.trend.title': 'Trend',
  'smartProfile.trend.first': 'First score recorded',
  'smartProfile.trend.sinceLast': 'since last update',
  'smartProfile.tips.title': 'Boost your score',
  'smartProfile.tips.allGood': 'Strong across the board — keep your profile current.',
  'smartProfile.tips.cta': 'Update my profile',
  'smartProfile.tips.completeness': 'Complete the missing profile sections to lift completeness.',
  'smartProfile.tips.skills': 'Add more skills and set their levels — verified skills count more.',
  'smartProfile.tips.experience': 'Add work experience and your total years of experience.',
  'smartProfile.tips.education': 'Add education or projects to round out your profile.',
  'smartProfile.tips.cv': 'Upload and analyze a CV to add CV readiness to your score.',
  'smartProfile.gap.title': 'Skill gap analysis',
  'smartProfile.gap.note': 'Curated role skills vs. your profile.',
  'smartProfile.gap.roleLabel': 'Target role',
  'smartProfile.gap.coverage': 'Role coverage',
  'smartProfile.gap.missing': 'Skills to learn',
  'smartProfile.gap.allMatched': 'You cover every skill for this role.',
  'smartProfile.matches.title': 'High-priority matches',
  'smartProfile.matches.empty': 'No published jobs match your skills yet.',
  'smartProfile.matches.missing': 'Missing',
  'smartProfile.recommendation.title': 'AI recommendation',
  'smartProfile.recommendation.refresh': 'Regenerate recommendation',
  'smartProfile.recommendation.sourceAi': 'AI',
  'smartProfile.recommendation.sourceRuleBased': 'Rule-based',
  'smartProfile.recommendation.suggestedRole': 'Suggested role',
  'smartProfile.recommendation.focus': 'Focus skills',
  'smartProfile.quickStats.title': 'Quick stats',
  'smartProfile.quickStats.skills': 'Skills',
  'smartProfile.quickStats.experience': 'Experience',
  'smartProfile.quickStats.education': 'Education',
  'smartProfile.quickStats.projects': 'Projects',
  'smartProfile.skillMap.title': 'Skill map',
  'smartProfile.skillMap.note': 'Self-reported levels from your profile.',
  'smartProfile.skillMap.empty': 'No skills added yet.',
  'smartProfile.skillMap.levelUnset': 'Level not set',
  'smartProfile.experience.title': 'Experience timeline',
  'smartProfile.experience.empty': 'No experience added yet.',
  'smartProfile.experience.current': 'Present',
  'smartProfile.education.title': 'Education',
  'smartProfile.education.empty': 'No education added yet.',
  'smartProfile.cvSignals.title': 'CV signals',
  'smartProfile.cvSignals.ats': 'ATS readiness',
  'smartProfile.cvSignals.detected': 'Detected skills',
  'smartProfile.reserved.label': 'Reserved',
  'smartProfile.reserved.note': 'Activates in a later approved phase.',
  'smartProfile.reserved.recommendation.title': 'AI recommendation',
  'smartProfile.reserved.recommendation.body':
    'Personalized next-role and growth guidance once the career-intelligence engine ships.',
  'smartProfile.reserved.gap.title': 'Skill gap analysis',
  'smartProfile.reserved.gap.body':
    'Gap-to-target-role analysis once the Skill Graph and target roles exist.',
  'smartProfile.reserved.matches.title': 'High-priority matches',
  'smartProfile.reserved.matches.body':
    'Explainable role matches once the AI matching engine and talent graph are live.',
  'smartProfile.avatar.alt': 'Profile picture',
  'smartProfile.avatar.change': 'Change photo',
  'smartProfile.avatar.remove': 'Remove photo',
  'smartProfile.editProfile': 'Edit profile',
  'smartProfile.image.uploading': 'Uploading…',
  'smartProfile.image.invalidType': 'Only PNG, JPEG, and WEBP images are supported.',
  'smartProfile.image.tooLarge': 'Image must be 5 MB or smaller.',
  'smartProfile.image.uploadError': 'Image could not be uploaded.',

  // Tier B — lightweight placeholders
  'preview.company.title': 'Company',
  'preview.company.body':
    'The employer side of Workerize — organization setup, job posting, and AI-ranked applicants — is a later approved phase. The candidate experience is the current focus.',
  'preview.settings.title': 'Settings',
  'preview.settings.body':
    'Workspace preferences and account controls will live here. For now, theme and language are available from the top bar and your user menu.',

  // Settings — Privacy & discoverability (real)
  'settings.title': 'Settings',
  'settings.subtitle': 'Manage your privacy and what employers can see.',
  'settings.loading': 'Loading settings…',
  'settings.saveError': 'Could not save your setting.',
  'settings.privacy.title': 'Privacy & discoverability',
  'settings.privacy.note': 'You control what employers can see. Privacy-first by default.',
  'settings.privacy.reservedNote':
    'The employer-facing surfaces that use these aren’t live yet — your choices are saved and apply when they ship.',
  'settings.privacy.discoverable.label': 'Discoverable to employers',
  'settings.privacy.discoverable.body': 'Let employers find your profile in talent search.',
  'settings.privacy.showEmployabilityScore.label': 'Show Employability Score',
  'settings.privacy.showEmployabilityScore.body':
    'Allow employers to see your Employability Score.',
  'settings.privacy.shareContactDetails.label': 'Share contact details',
  'settings.privacy.shareContactDetails.body':
    'Share your email and phone with employers you match with.',
  'settings.privacy.allowRecruiterMessages.label': 'Allow recruiter messages',
  'settings.privacy.allowRecruiterMessages.body': 'Let recruiters send you messages.',

  // Company — job postings (employer Hiring OS)
  'company.title': 'Jobs',
  'company.subtitle': 'Create and manage your job postings.',
  'company.loading': 'Loading jobs…',
  // Company sub-tabs + hiring pages
  'company.tab.jobs': 'Jobs',
  'company.tab.applications': 'Applications',
  'company.tab.candidates': 'Candidates',
  'company.tab.profile': 'Company profile',
  'company.applications.title': 'Applications',
  'company.applications.loading': 'Loading applications…',
  'company.applications.error': 'Could not load applications.',
  'company.applications.empty': 'No applications yet.',
  'company.applications.col.candidate': 'Candidate',
  'company.applications.col.job': 'Job',
  'company.applications.col.status': 'Status',
  'company.candidates.title': 'Find candidates',
  'company.candidates.search': 'Search',
  'company.candidates.searchPlaceholder': 'Search headline, summary, or skill',
  'company.candidates.skillsPlaceholder': 'Skills (comma-separated)',
  'company.candidates.privacyNote':
    'Only candidates who opted into discoverability appear here.',
  'company.candidates.empty': 'No matching candidates.',
  'company.candidates.makeOffer': 'Make offer',
  'company.candidates.offerTitle': 'Role / title',
  'company.candidates.offerComp': 'Compensation',
  'company.candidates.sendOffer': 'Send offer',
  'company.candidates.offerSent': 'Offer sent',
  'company.candidates.offerError': 'Could not send the offer.',
  'company.profile.title': 'Company profile',
  'company.profile.loading': 'Loading…',
  'company.profile.error': 'Could not load the company profile.',
  'company.profile.saved': 'Company profile saved.',
  'company.profile.saveError': 'Could not save the company profile.',
  'company.profile.name': 'Company name',
  'company.profile.about': 'About',
  'company.profile.website': 'Website',
  'company.profile.industry': 'Industry',
  'company.profile.size': 'Company size',
  'company.profile.location': 'Location',
  'company.profile.save': 'Save profile',
  'company.empty': 'No jobs yet — create your first posting above.',
  'company.form.title': 'New job posting',
  'company.form.jobTitle': 'Job title',
  'company.form.location': 'Location',
  'company.form.employmentType': 'Employment type',
  'company.form.workMode': 'Work mode',
  'company.form.skills': 'Required skills (comma-separated)',
  'company.form.description': 'Description',
  'company.form.submit': 'Create job',
  'company.form.titleRequired': 'A job title of at least 2 characters is required.',
  'company.createError': 'Could not create the job.',
  'company.actionError': 'Could not update the job.',
  'company.list.title': 'Your jobs',
  'company.action.publish': 'Publish',
  'company.action.close': 'Close',
  'company.action.delete': 'Delete',
  'company.employmentType.full_time': 'Full-time',
  'company.employmentType.part_time': 'Part-time',
  'company.employmentType.contract': 'Contract',
  'company.employmentType.internship': 'Internship',
  'company.employmentType.temporary': 'Temporary',
  'company.workMode.onsite': 'On-site',
  'company.workMode.remote': 'Remote',
  'company.workMode.hybrid': 'Hybrid',
  'company.status.draft': 'Draft',
  'company.status.published': 'Published',
  'company.status.closed': 'Closed',

  // Company — extended jobs (professional posting)
  'company.newJob': 'New job',
  'company.empty.body': 'Create your first posting to start receiving applications.',
  'company.form.new': 'New job posting',
  'company.form.edit': 'Edit job posting',
  'company.form.update': 'Save changes',
  'company.form.saved': 'Job saved.',
  'company.form.department': 'Department',
  'company.form.experienceLevel': 'Experience level',
  'company.form.educationLevel': 'Education level',
  'company.form.salaryMin': 'Salary (min)',
  'company.form.salaryMax': 'Salary (max)',
  'company.form.currency': 'Currency',
  'company.form.salaryPeriod': 'Pay period',
  'company.form.openings': 'Openings',
  'company.form.deadline': 'Application deadline',
  'company.form.visaSponsorship': 'Visa sponsorship',
  'company.form.visaSponsorship.yes': 'Offer visa sponsorship',
  'company.form.responsibilities': 'Responsibilities',
  'company.form.requirements': 'Requirements',
  'company.form.benefits': 'Benefits & perks',
  'company.action.edit': 'Edit',
  'company.action.reopen': 'Reopen',
  'company.experienceLevel.intern': 'Intern',
  'company.experienceLevel.entry': 'Entry level',
  'company.experienceLevel.mid': 'Mid level',
  'company.experienceLevel.senior': 'Senior',
  'company.experienceLevel.lead': 'Lead / Principal',
  'company.experienceLevel.executive': 'Executive',
  'company.educationLevel.none': 'No requirement',
  'company.educationLevel.high_school': 'High school',
  'company.educationLevel.associate': 'Associate degree',
  'company.educationLevel.bachelor': "Bachelor's degree",
  'company.educationLevel.master': "Master's degree",
  'company.educationLevel.doctorate': 'Doctorate',
  'company.salaryPeriod.hour': 'hr',
  'company.salaryPeriod.day': 'day',
  'company.salaryPeriod.month': 'mo',
  'company.salaryPeriod.year': 'yr',
  'company.job.openings': 'openings',
  'company.job.visa': 'Visa sponsorship',
  'company.delete.confirm': 'Delete this job posting?',

  // Company — applications (filter + actions)
  'company.applications.col.applied': 'Applied',
  'company.applications.updated': 'Application updated.',
  'company.applications.status.new': 'New',
  'company.applications.status.reviewing': 'Reviewing',
  'company.applications.status.offered': 'Offered',
  'company.applications.status.accepted': 'Accepted',
  'company.applications.status.rejected': 'Rejected',
  'company.applications.status.withdrawn': 'Withdrawn',
  'company.applications.action.review': 'Mark as reviewing',
  'company.applications.action.offer': 'Mark as offered',
  'company.applications.action.accept': 'Accept',
  'company.applications.action.reject': 'Reject',

  // Company — find candidates
  'company.candidates.clear': 'Clear',
  'company.candidates.complete': 'Profile completeness',
  'company.candidates.contactPrivate': 'Contact details private',
  'company.candidates.searchResults': 'Search results',
  'company.candidates.topResults': 'Top candidates',
  'company.candidates.yearsExp': 'yrs exp',
  'company.candidates.years': 'years',
  'gender.male': 'Male',
  'gender.female': 'Female',

  // Company — profile (rich fields)
  'company.profile.tagline': 'Tagline',
  'company.profile.tagline.placeholder': 'What your company is known for',
  'company.profile.mission': 'Mission',
  'company.profile.culture': 'Culture',
  'company.profile.founded': 'Founded',
  'company.profile.contactEmail': 'Contact email',
  'company.profile.contactPhone': 'Contact phone',
  'company.profile.linkedin': 'LinkedIn',
  'company.profile.twitter': 'X / Twitter',
  'company.profile.github': 'GitHub',
  'company.profile.benefits': 'Benefits & perks',
  'company.profile.benefits.placeholder': 'Health insurance, remote-first…',
  'company.profile.techStack': 'Tech stack',
  'company.profile.values': 'Values',
  'company.profile.values.placeholder': 'Ownership, transparency…',
  'company.profile.completeness': 'Profile completeness',
  'company.profile.section.story': 'Company story',
  'company.profile.section.facts': 'Company facts',
  'company.profile.section.contact': 'Contact & social',
  'company.profile.section.perks': 'Perks, stack & values',
  'company.profile.size.any': 'Select size',

  // Employer dashboard (redesigned)
  'employerDash.loading': 'Loading dashboard…',
  'employerDash.kpi.jobs': 'Total jobs',
  'employerDash.kpi.published': 'Published',
  'employerDash.kpi.applications': 'Applications',
  'employerDash.kpi.review': 'In review',
  'employerDash.kpi.offers': 'Offers',
  'employerDash.kpi.hired': 'Hired',
  'employerDash.chart.applications': 'Application pipeline',
  'employerDash.chart.jobs': 'Jobs by status',
  'employerDash.funnel.title': 'Hiring funnel',
  'employerDash.funnel.totalApplications': 'Total applications',
  'employerDash.funnel.applications': 'Applications',
  'employerDash.funnel.offers': 'Offers',
  'employerDash.funnel.hires': 'Hires',
  'employerDash.funnel.offerRate': 'Offer rate',
  'employerDash.funnel.hireRate': 'Hire rate',

  // Admin — early-access requests
  'adminEarlyAccess.title': 'Early-access requests',
  'adminEarlyAccess.subtitle': 'Review waitlist sign-ups and update their status.',
  'adminEarlyAccess.loading': 'Loading requests…',
  'adminEarlyAccess.empty': 'No early-access requests yet.',
  'adminEarlyAccess.updateError': 'Could not update the request.',
  'adminEarlyAccess.col.name': 'Name',
  'adminEarlyAccess.col.email': 'Email',
  'adminEarlyAccess.col.interestedAs': 'Interested as',
  'adminEarlyAccess.col.status': 'Status',
  'adminEarlyAccess.status.new': 'New',
  'adminEarlyAccess.status.reviewed': 'Reviewed',
  'adminEarlyAccess.status.invited': 'Invited',
  'adminEarlyAccess.status.approved': 'Approved',
  'adminEarlyAccess.status.rejected': 'Rejected',
  'adminEarlyAccess.finalized': 'Finalized',
  'adminEarlyAccess.approve': 'Approve',
  'adminEarlyAccess.reject': 'Reject',
  'adminEarlyAccess.updated': 'Request updated.',
  'preview.analytics.title': 'Analytics',
  'preview.analytics.body':
    'Analytics will appear after Workerize has real profile, CV, and activity history to summarize. No fabricated charts are shown before then.',
  'preview.integrations.title': 'Integrations',
  'preview.integrations.body':
    'GitHub, LinkedIn, and calendar integrations are planned for a later approved phase. No integration status is shown until they are real.',

} as const;

export const en = {
  ...appLevel,
  ...chrome,
  ...landingEn,
  ...landingPlaceholdersEn,
  ...authEn,
  ...earlyAccessEn,
} as const;

export type MessageKey = keyof typeof en;
export type Messages = Record<MessageKey, string>;
